import { Component, OnInit, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { capability } from '../../../shared/models/Capability';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { AdminService } from '../../../admin/admin.service';
import { TCCPCommonService } from '../../../shared/services/tccp-common.service';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { ManageCvsMappingService } from '../../manage-cvs-mapping/manage-cvs-mapping.service';
import { Router } from '@angular/router';
import { TCCPConstantValues } from '../../../shared/models/tccp-constants';
import { LoaderService } from '../../../shared/loader/loader.service';
import { MsalService } from '@azure/msal-angular';

@Component({
  selector: 'app-add-cvs-mapping',
  templateUrl: './add-cvs-mapping.component.html',
  styleUrls: ['./add-cvs-mapping.component.scss']
})
export class AddCvsMappingComponent implements OnInit {
  public pageTitle: string = null;
  public alerts: any[] = [];
  public capabilityForm: FormGroup;
  public capability: capability = <capability>{};
  public mode = 'add';
  public initialCapability: capability = <capability>{};
  public internalFeatureList: any[] = []; // To display selected internal support topics
  public externalFeatureList: any[] = []; // To display selected external support topics
  public internalSPList: any[] = [];
  public externalSPList: any[] = [];
  public selectedApplication: string = this.adminService.selectedApplication;
  public isSubmit = false;
  public invalidFeatureSelection = false;

  constructor(
    private fb: FormBuilder, private dialog: MatDialog, private adminService: AdminService,
    private mappingService: ManageCvsMappingService, public commonService: TCCPCommonService,
    private authService: MsalService, private router: Router, private loaderService: LoaderService) {
  }

  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    this.alerts = [];
    if ((this.mode === 'add' || this.mode === 'edit')) {
      const dialogRef = this.dialog.open(ConfirmModalComponent, {
        width: '350px',
        data: TCCPConstantValues.admin.messages.LOST_UNSAVED_MESSAGE,
        id: 'admin-' + this.adminService.selectedApplication,
        position: { top: '100px' }
      });
      return dialogRef.afterClosed();
    } else {
      return true;
    }
  }

  public hasError = (controlName: string, errorName: string, groupName?: string) => {
    return this.capabilityForm.controls[controlName].hasError(errorName);
  }

  ngOnInit() {
    this.capability = <capability>{};
    this.capabilityForm = this.fb.group(
      {
        capability: new FormControl(this.capability.capability, [Validators.required]),
        internalSTName: new FormControl(this.capability.internalSTName),
        externalSTName: new FormControl(this.capability.externalSTName)
      });
    this.getAllSupportTopicNames();
  }

  public showAlertMessages(messageType, message) {
    this.alerts = [];
    if (this.alerts.length === 0) {
      this.alerts.push({
        type: messageType,
        msg: message
      });
    }
    window.scroll(0, 0);
  }

  closeAlerts() {
    this.alerts = [];
  }

  getCapability() {
    const selectedApp = this.selectedApplication.charAt(0).toUpperCase() + this.selectedApplication.slice(1);
    this.mappingService.getCapabilityList(selectedApp, this.mappingService.selectedCapability).subscribe((response: any) => {
      if (response) {
        const selectedInternalFeatures = [];
        const selectedExternalFeatures = [];
        this.capability = response[0];
        this.capabilityForm.patchValue(this.capability);
        this.internalSPList.forEach(obj => {
          response[0].supportTopic.forEach(st => {
            if (obj.issueId === st.issueId) {
              selectedInternalFeatures.push(obj);
            }
          });
        });
        this.externalSPList.forEach(obj => {
          response[0].supportTopic.forEach(st => {
            if (obj.issueId === st.issueId) {
              selectedExternalFeatures.push(obj);
            }
          });
        });
        this.initialCapability = Object.assign({}, this.capability);
        this.capabilityForm.get('internalSTName').setValue(selectedInternalFeatures);
        this.capabilityForm.get('externalSTName').setValue(selectedExternalFeatures);
        this.externalFeatureList = selectedExternalFeatures;
        this.internalFeatureList = selectedInternalFeatures;
        this.loaderService.hide();
      }
    });
  }

  setPageMode(mode?: string) {
    this.loaderService.show();
    this.mode = this.mappingService.pageMode;
    if (mode && mode !== undefined) {
      this.mode = mode;
    }
    this.pageTitle = this.mode === 'edit' ? 'Edit CVS Mapping' : this.mode === 'add' ? 'Add CVS Mapping' : 'View CVS Mapping';
    this.capability = <capability>{};
    if ((this.mode === 'edit') || (this.mode === 'view')) {
      this.pageTitle = this.mode === 'edit' ? 'Edit CVS Mapping' : 'View CVS Mapping';

      this.getCapability();
      const capabilityKey = 'capability';
      this.capabilityForm.controls[capabilityKey].disable();
      if (this.mode === 'view') {
        this.capabilityForm.disable();
      }
    } else {
      this.pageTitle = 'Add CVS Mapping';
      this.loaderService.hide();
    }
    this.capabilityForm = this.fb.group(
      {
        capability: new FormControl(this.capability.capability, [Validators.required]),
        internalSTName: new FormControl(this.capability.internalSTName),
        externalSTName: new FormControl(this.capability.externalSTName)
      });
  }

  getAllSupportTopicNames() {
    this.adminService.getFlow().subscribe((data: any) => {
      if (data) {
        data.filter(control => {
          control.controls.filter(obj => {
            if (control.featureType === 'internal') {
              this.internalSPList.push({
                issueId: control.issueId, name: obj.featureName,
                type: control.featureType, display: obj.featureName
              });
            }
            if (control.featureType === 'external') {
              this.externalSPList.push({
                issueId: control.issueId, name: obj.featureName,
                type: control.featureType, display: obj.featureName
              });
            }
          });
        });
        this.setPageMode();
      }
    });
  }

  onInternalSupportTopicNameChange() {
    this.isSubmit = false;
    this.internalFeatureList = this.capabilityForm.get('internalSTName').value;
  }

  onExternalSupportTopicNameChange() {
    this.isSubmit = false;
    this.externalFeatureList = this.capabilityForm.get('externalSTName').value;
  }

  openDeleteConfirmDialog(item): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      width: '350px',
      data: 'Are you sure you want to delete the Support Topic?',
      position: { top: '100px' }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.onDelete(item);
      }
    });
  }

  onDelete(control) {
    const type = control.type === 'internal' ? 'internalSTName' : 'externalSTName';
    const selectedFeatures = this.capabilityForm.get(type).value;
    if (this.capabilityForm.get(type).value && this.capabilityForm.get(type).value.length > 0) {
      this.capabilityForm.get(type).value.filter((obj, index) => {
        if (obj && obj.issueId === control.issueId) {
          selectedFeatures.splice(index, 1);
        }
        this.capabilityForm.get(type).setValue(selectedFeatures);
      });
    }
  }

  addCapability(capabilityObj: capability) {
    this.mappingService.insertCVSCapability(capabilityObj).subscribe(response => {
      if (response) {
        this.mappingService.selectedCapability = capabilityObj.capability;
        this.mode = 'view';
        this.capabilityForm.disable();
        this.showAlertMessages('success', TCCPConstantValues.admin.messages.CVS_MAPPING_SAVE_SUCCESS_MESSAGE);
      }
    });
  }

  updateCapability(capabilityObj: capability) {
    const selectedApp = this.selectedApplication.charAt(0).toUpperCase() + this.selectedApplication.slice(1);
    const requestData = [];
    requestData.push(capabilityObj);
    this.mappingService.updateCVSCapability(capabilityObj, this.capabilityForm.get('capability').value,
      selectedApp).subscribe(response => {
        if (response) {
          this.mappingService.selectedCapability = capabilityObj.capability;
          this.mode = 'view';
          this.capabilityForm.disable();
          this.showAlertMessages('success', TCCPConstantValues.admin.messages.CVS_MAPPING_SAVE_SUCCESS_MESSAGE);
        }
      });
  }

  onMandatoryCheck() {
    this.invalidFeatureSelection = this.internalFeatureList.length === 0 && this.externalFeatureList.length === 0;
    return this.internalFeatureList.length === 0 && this.externalFeatureList.length === 0 ? false : true;
  }

  onSave(capabilityForm) {
    const internalSTNameKey = 'internalSTName';
    const externalSTNameKey = 'externalSTName';
    this.isSubmit = true;
    this.capabilityForm.markAllAsTouched();
    if (this.onMandatoryCheck()) {
      let internalSupportTopic = [];
      let externalSupportTopic = [];
      if (this.capabilityForm.controls[internalSTNameKey].value && this.capabilityForm.controls[internalSTNameKey].value.length > 0) {
        internalSupportTopic = this.capabilityForm.controls[internalSTNameKey].value.map(x => {
          return { issueId: x.issueId, name: x.name, display: x.display, type: x.type };
        });
      }
      if (this.capabilityForm.controls[externalSTNameKey].value && this.capabilityForm.controls[externalSTNameKey].value.length > 0) {
        externalSupportTopic = this.capabilityForm.controls[externalSTNameKey].value.map(x => {
          return { issueId: x.issueId, name: x.name, display: x.display, type: x.type };
        });
      }
      const supportTopic = internalSupportTopic.concat(externalSupportTopic);
      this.alerts = [];
      const selectedApp = this.selectedApplication.charAt(0).toUpperCase() + this.selectedApplication.slice(1);
      this.capabilityForm.markAllAsTouched();
      if (this.capabilityForm.valid) {
        const requestData: capability = <capability>{};
        requestData.capability = this.capabilityForm.get('capability').value;
        requestData.createdBy = this.authService.instance.getActiveAccount().username;
        requestData.updatedBy = this.authService.instance.getActiveAccount().username;
        requestData.division = selectedApp;
        requestData.supportTopic = supportTopic;
        requestData.updatedAt = '';
        requestData.createdAt = '';
        requestData.capabilityDesc = '';
        this.capability = Object.assign(this.capability, this.capabilityForm.value);
        if (this.mode === 'add') {
          this.isSubmit = false;
          this.addCapability(requestData);
        } else if (this.mode === 'edit') {
          this.isSubmit = false;
          this.updateCapability(requestData);
        }
      }
    }
  }

  onEdit() {
    const capabilityKey = 'capability';
    this.alerts = [];
    this.mode = 'edit';
    this.pageTitle = 'Edit CVS Mapping';
    this.capabilityForm.enable();
    this.capabilityForm.controls[capabilityKey].disable();
  }

  onCancel() {
    this.alerts = [];
    this.openCancelDialog();
  }

  goToMappingList() {
    this.router.navigateByUrl('/admin/manage-cvs-mapping');
  }

  openCancelDialog(): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      width: '350px',
      data: TCCPConstantValues.admin.messages.LOST_UNSAVED_MESSAGE,
      id: 'admin-' + this.adminService.selectedApplication
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isSubmit = false;
        this.capabilityForm.markAsUntouched();
        if (this.mode === 'add') {
          this.capabilityForm.reset();
          this.internalFeatureList = [];
          this.externalFeatureList = [];
        } else {
          this.mode = 'view';
          this.setPageMode('view');
          this.capabilityForm.disable();
        }
      }
    });
  }

}
